<template>
  <div>
    <b-notification
        v-model="isError"
        type="is-danger"
        aria-close-label="Close notification"
        :closable="false">
        <b>Error fetching tip!</b><br />Try refreshing the page. If the error persists check your network connection or let BM know.
    </b-notification>
    <b-loading :is-full-page="true" v-model="isLoading"></b-loading>

    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      v-if="!isLoading && !isError"
      parentTitle="Tips & Encouragements"
      :currentTitle="title"
      :backRoute="{ name: 'TipsEncouragementListView' }"
    >
      <FeedsStatusBadge :status="tip.status" />
      <b-button outlined type="is-primary" size="is-small" icon-left="check" @click="saveAndClose">Save & Close</b-button>
      <b-button type="is-primary" size="is-small" icon-left="content-save" @click="save">Save</b-button>
    </TopBar>

    <div v-if="!isLoading && !isError" class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ title }}</h1>
          <p class="page-subtitle">Tip & Encouragement Editor</p>
        </div>
      </div>

      <div class="tip-content-grid">
        <!-- Form Card -->
        <FeedsCard title="Content">
          <div class="form-group">
            <label class="form-label">Status</label>
            <b-select v-model="tip.status" placeholder="Status" expanded @input="save">
              <option>draft</option>
              <option>test</option>
              <option>fix</option>
              <option>live</option>
            </b-select>
          </div>
          <div class="form-group">
            <label class="form-label">Title</label>
            <b-input v-model="tip.title" @input="saveDebounced" />
          </div>
          <div class="form-group">
            <label class="form-label">Body</label>
            <editor v-model="tip.body" @input="saveDebounced"/>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Background</label>
              <b-select placeholder="Choose colour" icon="format-color-fill"
                v-model="tip.background_color"
                expanded
                @input="saveDebounced">
                <option
                      v-for="option in colors"
                      :value="option"
                      :key="option.name">
                      {{ option.name }}
                </option>
              </b-select>
            </div>
            <div class="form-group">
              <label class="form-label">Foreground</label>
              <b-select placeholder="Choose colour" icon="palette"
                v-model="tip.foreground_color"
                expanded
                @input="saveDebounced">
                <option
                      v-for="option in colors"
                      :value="option"
                      :key="option.name">
                      {{ option.name }}
                </option>
              </b-select>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Sticker</label>
            <div class="sticker-row">
              <figure v-if="stickerSrc" class="sticker-preview">
                <img :src="stickerSrc" />
              </figure>
              <ImageUpload
                v-model="tip.sticker"
                :isSticker="true"
                buttonTitle="Update Sticker"
                @input="save"
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Sticker Position</label>
            <b-select v-model="tip.sticker_position" placeholder="Position" expanded @input="saveDebounced">
              <option>top-left</option>
              <option>top-right</option>
              <option>top-middle</option>
              <option>bottom-left</option>
              <option>bottom-right</option>
              <option>bottom-middle</option>
            </b-select>
          </div>
        </FeedsCard>

        <!-- Preview Card -->
        <FeedsCard title="Preview">
          <div class="preview-background" :style="backgroundStyle">
            <div class="preview-foreground"
                :class="cardFlexDirection"
                :style="foregroundStyle">
              <figure class="preview-sticker" :style="stickerStyle">
                <img v-if="stickerSrc" :src="stickerSrc" />
              </figure>
              <div class="preview-text">
                <h1 :style="foregroundTextStyle">{{ tip.title }}</h1>
                <div :style="foregroundTextStyle" v-html="tip.body" />
              </div>
            </div>
          </div>
        </FeedsCard>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import Editor from "@/components/BaseEditor"
import db from "@/firebase/db"
import Colours from "@/models/Colours"
import ImageUpload from "@/components/ImageUpload"
import FeedsCard from "@/components/common/FeedsCard"
import FeedsStatusBadge from "@/components/common/FeedsStatusBadge"
import TopBar from "@/components/layout/TopBar"

export default {
  name: 'TipEncouragementView',
  components: {
    Editor,
    ImageUpload,
    FeedsCard,
    FeedsStatusBadge,
    TopBar
  },
  props: [
    'id'
  ],
  data() {
    return {
      isLoading: true,
      isError: false,
      tip: {}
    };
  },
  computed: {
    title() {
      return this.tip.title ?? "New Tip";
    },
    stickerSrc() {
      return (this.tip.sticker != null) ? this.tip.sticker.src : null;
    },
    colors() {
      return Colours.all;
    },
    backgroundStyle() {
      if (this.tip.background_color == null) {
        return { backgroundColor: "#333333" }
      }
      return { backgroundColor: this.tip.background_color.colour }
    },
    foregroundStyle() {
      if (this.tip.foreground_color == null) {
        return { backgroundColor: "#F2F9EE" }
      }
      return { backgroundColor: this.tip.foreground_color.colour }
    },
    foregroundTextStyle() {
      if (this.tip.foreground_color == null) {
        return { color: "#333333"}
      }
      return { color: this.tip.foreground_color.invertTheme == "dark" ? "#333333":"#F2F9EE",}
    },
    cardFlexDirection(){
      if (this.tip.sticker_position == null) {
        return "flex-col-reverse"
      }
      return this.tip.sticker_position.includes('bottom') ? 'flex-col-reverse':'flex-col';
    },
    stickerStyle() {
      if (this.tip.sticker_position == null) {
        return null;
      }
      if (this.tip.sticker_position.includes("left")){
        return { textAlign: "left"};
      } else if (this.tip.sticker_position.includes("right")){
        return { textAlign: "right"};
      } else{
        return { textAlign: "center"};
      }
    }
  },
  created: function () {
    db.collection('tips_encouragements')
      .doc(this.id)
      .get()
      .then(snapshot => {
        if (snapshot.exists) {
          this.tip = snapshot.data();
        }else {
          this.isError = true;
        }
        this.isLoading = false;
      })
      .catch((error) => {
        this.isError = true;
        this.$buefy.notification.open({type: 'is-danger', message: `Error fetching TipEncouragement: ${error}`})
      });
  },
  methods: {
    saveDebounced: debounce(function () {
      this.save()
    }, 1000),
    async saveAndClose(){
      this.save()
        .then(() => {
          this.$router.go(-1)
        })
    },
    async save(){
      db.collection('tips_encouragements')
        .doc(this.id)
        .set(this.tip)
        .then(() => {
          this.$buefy.toast.open({type: 'is-success', message: `Tip successfully updated!`})
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error saving Tip: ${error}`})
        });
    },
  }
}
</script>

<style scoped>
.tip-content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  align-items: start;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.sticker-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sticker-preview {
  margin: 0;
}

.sticker-preview img {
  max-width: 80px;
  max-height: 80px;
}

.preview-background {
  padding: 20px;
  border-radius: var(--radius-md);
  min-height: 300px;
}

.preview-foreground {
  border-radius: 16px;
  padding: 16px;
  display: flex;
  min-height: 260px;
}

.preview-foreground.flex-col {
  flex-direction: column;
}

.preview-foreground.flex-col-reverse {
  flex-direction: column-reverse;
}

.preview-sticker {
  margin: 0;
}

.preview-sticker img {
  max-width: 100px;
  max-height: 100px;
}

.preview-text {
  padding: 12px;
}

.preview-text h1 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}
</style>
